import axios from 'axios';
import { API_BASE_URL } from '../config';

// Roles allowed to use the chat admin panel.
const PANEL_ROLES = ['OWNER', 'ADMIN'];

// localStorage keys (prefixed 'tenantAdmin' to avoid clashing with the chat app / platform admin)
const TOKEN_KEY = 'tenantAdminToken';
const USER_KEY = 'tenantAdminUser';
const MEMBERSHIP_KEY = 'tenantAdminMembership';
const TENANT_KEY = 'tenantAdminTenant';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Read { tenantId, scriptHash } from the current URL path.
// Pattern mirrors the chat widget: /<tenantId 6-12 alnum>/<scriptHash 16 hex>/...
export const getChatFromPath = () => {
  const match = window.location.pathname.match(/^\/([a-z0-9]{6,12})\/([a-f0-9]{16})/);
  if (!match) return null;
  return { tenantId: match[1], scriptHash: match[2] };
};

// Build the login URL for the current (or a given) chat.
export const chatLoginPath = (chat) => {
  const c = chat || getChatFromPath();
  if (!c) return '/';
  return `/${c.tenantId}/${c.scriptHash}/login`;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid: clear session and bounce to the chat login.
      authAPI.clearSession();
      window.location.href = chatLoginPath();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Login against the path-scoped tenant API. `username` accepts a chat-scoped
  // username OR an email. Enforces the OWNER/ADMIN panel gate before persisting.
  login: async (tenantId, scriptHash, { username, password }) => {
    const response = await api.post(
      `/tenant/${tenantId}/${scriptHash}/auth/login`,
      { username, password }
    );
    const { user, token, membership, tenant } = response.data.data;

    // Panel access gate: only OWNER or ADMIN may manage the chat.
    if (!membership || !PANEL_ROLES.includes(membership.role)) {
      const gateError = new Error(
        'This account is not authorized to manage this chat'
      );
      gateError.authGate = true;
      throw gateError;
    }

    // Persist session only after passing the gate.
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(membership));
    if (tenant) {
      localStorage.setItem(TENANT_KEY, JSON.stringify(tenant));
    }

    return { user, token, membership, tenant };
  },

  clearSession: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(MEMBERSHIP_KEY);
    localStorage.removeItem(TENANT_KEY);
  },

  logout: () => {
    authAPI.clearSession();
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  getMembership: () => {
    const str = localStorage.getItem(MEMBERSHIP_KEY);
    return str ? JSON.parse(str) : null;
  },

  getTenant: () => {
    const str = localStorage.getItem(TENANT_KEY);
    return str ? JSON.parse(str) : null;
  },

  // Authenticated only when a token is present AND the stored membership role
  // is one allowed to use the panel.
  isAuthenticated: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const membership = authAPI.getMembership();
    return !!token && !!membership && PANEL_ROLES.includes(membership.role);
  },
};

// Settings API (chat settings — name/description + whitelisted settings keys)
export const settingsAPI = {
  get: async (tenantId, scriptHash) => {
    const response = await api.get(`/tenant/${tenantId}/${scriptHash}/admin/settings`);
    return response.data.data;
  },

  update: async (tenantId, scriptHash, payload) => {
    const response = await api.patch(
      `/tenant/${tenantId}/${scriptHash}/admin/settings`,
      payload
    );
    return response.data.data;
  },
};

// Users API (chat memberships)
export const usersAPI = {
  list: async (tenantId, scriptHash, params = {}) => {
    const response = await api.get(`/tenant/${tenantId}/${scriptHash}/admin/users`, {
      params,
    });
    return response.data.data;
  },

  // NOTE: membershipId is the membership `id` from the users list (NOT userId).
  update: async (tenantId, scriptHash, membershipId, payload) => {
    const response = await api.patch(
      `/tenant/${tenantId}/${scriptHash}/admin/users/${membershipId}`,
      payload
    );
    return response.data.data;
  },

  // Reset (set) a member's password. `membershipId` is the membership `id`.
  resetPassword: async (tenantId, scriptHash, membershipId, { password }) => {
    const response = await api.put(
      `/tenant/${tenantId}/${scriptHash}/admin/users/${membershipId}/password`,
      { password }
    );
    return response.data;
  },
};

// Error handler utility
export const handleAPIError = (error) => {
  if (error.authGate) {
    return error.message;
  } else if (error.response) {
    return error.response.data.error || 'An error occurred';
  } else if (error.request) {
    return 'Network error. Please check your connection.';
  } else {
    return error.message || 'An unexpected error occurred';
  }
};

export default api;
